import { httpClient } from "infra/axios/axios-api";
import { Parameter } from "domain/entities/parameter";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { translateText } from "infra/translation/translate";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputCurrencyComponent from "presentation/components/Shared/InputCurrencyComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import Spinner from "presentation/components/Shared/Spinner";
import { useCreateExpenseMutation } from "presentation/hooks/use-query-expense";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useState } from "react";
import "../../index.css";
import { MandatorySignal } from "presentation/components/Shared/MandatorySignalComponent";
import InputTextAreaComponent from "presentation/components/Shared/InputTextAreaComponent";
import { Checkbox } from "primereact/checkbox";

interface ParamProps {
  isDisable: boolean;
  onClose?: () => void;
  toastRef?: RefObject<Toast | null>;
}

interface ExpenseParams {
  date: Date;
  value: number;
  type: Parameter;
  description: string;
  hasNimberNIF: boolean;
}

const ExpenseForm: React.FC<ParamProps> = (params: ParamProps) => {
  const { email: userEmployeeEmail, userEmployee: userEmployee } =
    useAuthStore();
  const { setExpenseType, expenseType } = useParameterStore();

  const {
    mutate: createExpenseMutation,
    isError: isCreateExpenseError,
    isSuccess: isCreateExpenseSuccess,
    isPending: isCreateExpensePending,
  } = useCreateExpenseMutation();

  const { data: parameters } = useParameterByFilter({
    limit: 1000,
    sortBy: "value",
  } as BaseInputFilter);

  const [contaboResponse, setContaboResponse] = useState<any>();
  const [isContaboPending, setIsContaboPending] = useState(false);
  const [doc, setDoc] = useState<{
    file: File;
    name: string;
    url: string;
  } | null>(null);
  const [expenseParameters, setExpenseParameters] = useState<ExpenseParams>();
  const [showDoc, setShowDoc] = useState(false);

  useEffect(() => {
    setExpenseType(
      parameters?.data?.filter((item) => item.type === "expenseType"),
    );
  }, [parameters]);

  useEffect(() => {
    if (contaboResponse && expenseParameters?.date && expenseParameters.type) {
      const filePath = contaboResponse.data.filePath;

      // Prepare input for creation
      const expenseToCreate = {
        filePath: filePath,

        date: expenseParameters.date,
        value: expenseParameters.value,
        type: expenseParameters.type,
        employeeId: userEmployee?.id,
        lastModifiedBy: userEmployeeEmail,
        status: "Pendente",
        description: expenseParameters.description,
        hasNimberNIF: expenseParameters.hasNimberNIF || false,
      };

      setIsContaboPending(false);

      createExpenseMutation(expenseToCreate);
    }
  }, [contaboResponse]);

  useEffect(() => {
    if (isCreateExpenseError) {
      params.toastRef?.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Falha ao guardar despesa",
        life: 1500,
      });
    }
  }, [isCreateExpenseError]);

  useEffect(() => {
    if (isCreateExpenseSuccess) {
      params.toastRef?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Despesa guardada com sucesso",
        life: 1500,
      });

      params.onClose?.();
    }
  }, [isCreateExpenseSuccess]);

  const handleSave = async () => {
    if (doc && expenseParameters?.date && expenseParameters.type) {
      // Upload logic
      const month = new Date(expenseParameters.date).getMonth() + 1;
      const year = new Date(expenseParameters.date).getFullYear();

      const formData = new FormData();

      const extension = doc.name.split(".").pop();
      // Since we don't have documentType.id anymore, we can use a generic string or removing it from filename logic if not critical.
      // Using "expense" as a placeholder ID substitute to keep format consistent if needed, or simplifing.
      const safeDocName = `${month}_${year}_expense_${Date.now()}`;

      const fileNameWithExtension = `${safeDocName}.${extension}`;
      formData.append("file", doc.file, fileNameWithExtension);

      try {
        setIsContaboPending(true);

        const response = await httpClient.post(
          `/assets/expenses/${userEmployee?.id}`,
          formData,
        );

        setContaboResponse(response);
      } catch (error) {
        console.error("Erro no upload:", error);
        setIsContaboPending(false);
        params.toastRef?.current?.show({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao fazer upload do documento",
          life: 1500,
        });
      }
    }
  };

  const handleUpload = async (event: any) => {
    const file = event.files[0];
    if (!file) return;

    setDoc({
      file,
      name: file.name,
      url: URL.createObjectURL(file),
    });
  };

  const removeDocument = () => {
    setDoc(null);
  };

  const openDocument = () => {
    if (doc?.url) setShowDoc(true);
  };

  if (isCreateExpensePending || isContaboPending) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="default-grid header-spacing">
        <div className="default-2-cols">
          <DropdownComponent
            options={expenseType}
            label={`${translateText(
              "Tipo de Despesa",
              "pt",
            )} ${MandatorySignal()}`}
            fieldName="type"
            currentObject={expenseParameters}
            getValue={expenseParameters?.type}
            setValue={setExpenseParameters}
          />
        </div>

        <div className="default-1-col">
          <InputCurrencyComponent
            label="Valor"
            fieldName="value"
            currentObject={expenseParameters}
            getValue={expenseParameters?.value}
            setValue={setExpenseParameters}
          />
        </div>

        <div className="default-1-col">
          <InputDateComponent
            label="Data da Despesa"
            fieldName="date"
            dateFormat="dd/mm/yy"
            currentObject={expenseParameters}
            getValue={expenseParameters?.date}
            setValue={setExpenseParameters}
          />
        </div>

        <div className="default-4-cols">
          <InputTextAreaComponent
            label={translateText("Description", "pt")}
            fieldName="description"
            currentObject={expenseParameters}
            getValue={expenseParameters?.description}
            setValue={setExpenseParameters}
          />
        </div>

        <div className="default-4-cols">
          <div className="flex align-items-center" style={{ gap: "0.5rem" }}>
            <Checkbox
              inputId="hasNimberNIF"
              checked={expenseParameters?.hasNimberNIF || false}
              onChange={(e) =>
                setExpenseParameters(
                  (prev) =>
                    ({
                      ...prev,
                      hasNimberNIF: e.checked || false,
                    }) as ExpenseParams,
                )
              }
            />
            <label htmlFor="hasNimberNIF" className="ml-2">
              A fatura tem o NIF da Nimber
            </label>
          </div>
        </div>

        {/* Caixa do documento */}
        <div className="default-4-cols">
          <div className="flex flex-column align-items-center border-1 document-border-color p-3">
            {doc ? (
              <>
                <p className="font-bold">{translateText(doc.name, "pt")}</p>
                <div className="spacing-between-buttons">
                  <Button
                    label={translateText("Ver", "pt")}
                    icon="pi pi-eye"
                    severity="secondary"
                    className="p-button-sm p-button-outlined"
                    onClick={openDocument}
                  />
                  <Button
                    label={translateText("Remover", "pt")}
                    icon="pi pi-trash"
                    className="p-button-sm p-button-outlined"
                    severity="danger"
                    onClick={removeDocument}
                  />
                </div>
              </>
            ) : (
              <FileUpload
                mode="basic"
                accept=".pdf,.jpg,.png"
                maxFileSize={10000000}
                customUpload
                auto
                uploadHandler={handleUpload}
                chooseLabel={translateText("Upload", "pt")}
                className="p-mt-2"
              />
            )}
          </div>
        </div>

        <div className="default-4-cols">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              label="Adicionar"
              onClick={handleSave}
              disabled={
                !expenseParameters?.date || !expenseParameters?.type || !doc
              }
            />
          </div>
        </div>
      </div>

      <Dialog
        header="Visualização do Documento"
        visible={showDoc}
        style={{ width: "80vw", height: "90vh" }}
        onHide={() => {
          setShowDoc(false);
        }}
      >
        {doc &&
          (doc.name.includes(".pdf") ? (
            <iframe
              src={`${doc.url}#navpanes=0`}
              width="100%"
              height="700px"
              title="Expense"
              style={{ border: "none" }}
            ></iframe>
          ) : (
            <img
              src={doc.url}
              width="100%"
              height="auto"
              title="Expense"
              style={{ border: "none" }}
            ></img>
          ))}
      </Dialog>
    </div>
  );
};
export default ExpenseForm;
